import { useEffect, useState } from 'react';
import { Tooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { smallAnonymizeCardNumber, handleCheckedUserCreditCards } from '@/utils/cardUtils';

import CreditCardLogo from '@/components/account/cards/CreditCardLogo';
import Icon from '@/components/common/iconedLinks/Icon';
import InputCvc from '@/components/checkout/payMode/InputCvc';
import { URLS } from '@/constants/urls';

export const HipaySavedCards = ({
  t, updateCardInfo, isActive, creditCards
}) => {
  const [selectedCard, setSelectedCard] = useState(creditCards.find((cc) => cc.isDefault) || creditCards[0]);
  const [userCreditCards, setUserCreditCards] = useState(handleCheckedUserCreditCards(creditCards));
  const [showCvcTooltip, setShowCvcTooltip] = useState(false);
  const [cvcIsValid, setCvcIsValid] = useState(false);

  const isMobile = useScreenSizeContext().isMobile;

  useEffect(() => {
    if (isActive) {
      updateCardInfo(cvcIsValid ? selectedCard : undefined);
    } else {
      setCvcIsValid(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard, cvcIsValid, isActive]);

  const selectSavedCreditCard = (selectedCreditCard) => {
    if (selectedCreditCard) {
      setUserCreditCards(userCreditCards.map((card) => {
        const checked = card.token === selectedCreditCard.token;
        if (checked) {
          setSelectedCard(card);
          if (!card.checked) {
            // If we select another saved card, the step is not valid anymore
            setCvcIsValid(false);
          }
        }
        return { ...card, checked };
      }));
    }
  };

  return userCreditCards.length > 0 ? (
    <>
      <div className="saved-card-container" data-test-id="hipay-saved-cards-container">
        <h4 className="saved-cards" data-test-id="hipay-saved-cards-title">
          {t('checkout:payMode.yourPaymentCards')}
        </h4>
        <div className="saved-card-row-header">
          <div className="row">
            <div className="col-md-4 creditCard d-none d-md-block" data-test-id="hipay-saved-cards-titlecreditCard">
              {t('checkout:payMode.paymentCards')}
            </div>
            <div className="col-md-3 cardHolder d-none d-md-block" data-test-id="hipay-saved-cards-header-cardHolder">
              {t('checkout:payMode.cardHolder')}
            </div>
            <div className="col-md-2 cardExpiry d-none d-md-block" data-test-id="hipay-saved-cards-header-expiryDate">
              {t('checkout:payMode.expiryDate')}
            </div>
            <div className="col-md-3 cardExpiry d-none d-md-block" data-test-id="hipay-saved-cards-header-cvc">
              {t('checkout:payMode.cvc')}
              <Icon src={URLS.ICON_QUESTION_HELP} alt="question" id="cvcSavedCreditCardTooltip" />
              <Tooltip placement="right" isOpen={showCvcTooltip} target="cvcSavedCreditCardTooltip"
                toggle={() => setShowCvcTooltip(!showCvcTooltip)} autohide={false}>
                {t('checkout:payMode.cvcTooltip')}
              </Tooltip>
            </div>
          </div>
        </div>
        {userCreditCards.map((card) => (
          <div
            key={card.token}
            className={`saved-card-row selectable${card.checked ? ' selected' : ''} tu-saved-card-row`}
            onClick={() => selectSavedCreditCard(card)}
            data-test-id="hipay-saved-cards-row"
          >
            <div className="row">
              <div className="col-md-4 col-item">
                {/* We must have the onChange attribute in order to use the checked attribute
                  and change the value programmatically */
                }
                <input
                  type="radio"
                  name="userCreditCardsRadio"
                  className="saved-card-radio"
                  onChange={() => undefined}
                  checked={card.checked}
                />
                <span className="mr-3">
                  <CreditCardLogo brand={card.brand} small={true} />
                </span>
                {smallAnonymizeCardNumber(card.pan)}
              </div>
              <div className="col-md-3 col-item" data-test-id="hipay-saved-cards-cardHolder">
                {card.cardHolder}
              </div>
              <div className="col-md-2 col-item" data-test-id="hipay-saved-cards-expiryDate">
                {card.cardExpireMonth}/{card.cardExpireYear?.slice(-2)}
              </div>
              <div className="col-md-3 col-item col-item-cvc" data-test-id="hipay-saved-cards-cvc">
                {card.checked && (
                  cvcIsValid ? (
                    <div className="validated">
                      <Icon src={URLS.ICON_CHECKMARK_GREEN} alt="checkmark" />
                      {t('checkout:payMode.validated')}
                    </div>
                  ) : (
                    <div className="d-flex">
                      <InputCvc
                        t={t}
                        cardInfo={selectedCard}
                        setCvcIsValid={setCvcIsValid}
                        showPlaceholder={isMobile}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  ) : <></>;
};

HipaySavedCards.propTypes = {
  t: PropTypes.func.isRequired,
  updateCardInfo: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  creditCards: PropTypes.array.isRequired,
};

export default HipaySavedCards;
